import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared styling. Fonts are deliberately enlarged (question feedback Rq4):
// questions text-xl (~20px), options/inputs text-lg (~18px) for older respondents.
const INPUT_CLASS = 'text-lg border-[#E2E8F0] focus:ring-[#0F203C]/20 focus:border-[#0F203C]';

// Labels of the free-text "Autre / Une autre raison" options, so we can show a
// "Précisez" field when they are selected (Rq3 + Rq10).
const OTHER_LABELS = ['Autre', 'Une autre raison', 'Autre raison'];

const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const isFilledArr = (v) => Array.isArray(v) && v.length > 0;
const hasOther = (arr) => Array.isArray(arr) && arr.some((v) => OTHER_LABELS.includes(v));

// Map a field key to the id of the question block it belongs to (for error
// anchoring/highlighting). Matrix and multi-input questions share one anchor.
const anchorOf = (key) => {
  if (key.startsWith('q12_')) return 'q12';
  if (key.startsWith('q17_')) return 'q17';
  if (key === 'q10_montant' || key === 'q10_nombre') return 'q10';
  if (key.startsWith('rs10_')) return 'rs10';
  return key;
};

// Keys owned by each Q7 branch, cleared when the Q7 answer changes so a
// respondent who backtracks never submits stale answers from the other branch.
const THEME2_KEYS = [
  'q8', 'q8_autre', 'q9', 'q10_montant', 'q10_nombre', 'q11',
  'q12_comprehension', 'q12_dossier', 'q12_accord', 'q12_trouver', 'q12_devis', 'q12_intervention',
  'q13', 'q13_autre', 'q15', 'q_efficacite', 'q_inefficacite', 'q_inefficacite_autre',
  'q16', 'q16_autre', 'q17_stockage', 'q17_savoir_quand', 'q17_manipulation', 'q17_acces', 'q17_entretien',
  'q18', 'q19', 'q20', 'q21', 'q22', 'q23',
];
const THEME3_KEYS = ['q24', 'q25', 'q25_autre', 'q26', 'q27'];

// Given a field that just changed, list the dependent keys whose question is no
// longer shown, so their now-stale answers are dropped from formData.
const dependentsToClear = (field, value) => {
  switch (field) {
    case 'q5': return value !== 'Oui' ? ['q6'] : [];
    case 'q7': return [
      ...(value !== 'Oui' ? THEME2_KEYS : []),
      ...(value !== 'Non' ? THEME3_KEYS : []),
    ];
    case 'q15': return value !== 'Oui' ? ['q_efficacite', 'q_inefficacite', 'q_inefficacite_autre'] : [];
    case 'q_efficacite':
      return typeof value === 'string' && value.startsWith('Non') ? [] : ['q_inefficacite', 'q_inefficacite_autre'];
    case 'rs1': return value !== 'Un particulier' ? ['rs2', 'rs2_autre', 'rs3', 'rs3_autre', 'rs4', 'rs5'] : [];
    case 'rs7': return value !== 'Oui' ? ['rs8'] : [];
    case 'rs2': return value !== 'Autre' ? ['rs2_autre'] : [];
    case 'rs3': return value !== 'Autre' ? ['rs3_autre'] : [];
    case 'rs9': return value !== 'Autre' ? ['rs9_autre'] : [];
    // Multi-selects: drop the "Précisez" text once "Autre" is unticked.
    case 'q8': case 'q13': case 'q16': case 'q25': case 'q_inefficacite':
      return hasOther(value) ? [] : [`${field}_autre`];
    default: return [];
  }
};

const Survey = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(new Set());

  const updateField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Drop answers to questions this change has hidden (no stale submissions).
      dependentsToClear(field, value).forEach((k) => { delete next[k]; });
      return next;
    });
    // Live-clear the error on the question this field belongs to.
    setErrors((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      next.delete(anchorOf(field));
      return next;
    });
  };

  // Build the steps first so validators can target the current one.
  const steps = [
    { title: 'Question filtre', validate: validateFilter, render: (props) => <FilterQuestion {...props} /> },
    {
      title: 'Votre situation personnelle par rapport au risque',
      validate: validateTheme1,
      render: (props) => <Theme1Questions {...props} />,
    },
  ];

  if (formData.q7 === 'Oui') {
    steps.push({
      title: 'Acquisition des batardeaux et protection de votre bien',
      validate: validateTheme2,
      render: (props) => <Theme2Questions {...props} />,
    });
  } else if (formData.q7 === 'Non') {
    steps.push({
      title: 'Raisons de la non-acquisition de batardeaux',
      validate: validateTheme3,
      render: (props) => <Theme3Questions {...props} />,
    });
  }

  steps.push({
    title: 'Renseignements signalétiques',
    validate: validateDemographics,
    render: (props) => <DemographicQuestions {...props} />,
  });

  const focusFirstError = (missing) => {
    setErrors(new Set(missing));
    const el = document.getElementById(`q-${missing[0]}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = () => {
    if (currentStep === 0 && formData.q0 === 'Non') {
      toast.error("Vous devez avoir réalisé un diagnostic de vulnérabilité pour participer à cette enquête");
      return;
    }
    const missing = steps[currentStep].validate(formData);
    if (missing.length > 0) {
      toast.error('Merci de répondre aux questions obligatoires (marquées d\'une *).');
      focusFirstError(missing);
      return;
    }
    setErrors(new Set());
    setCurrentStep((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    setErrors(new Set());
    setCurrentStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const missing = steps[currentStep].validate(formData);
    if (missing.length > 0) {
      toast.error('Merci de répondre aux questions obligatoires (marquées d\'une *).');
      focusFirstError(missing);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/survey`, formData);
      toast.success('Merci pour votre participation !');
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const stepProps = { formData, updateField, errors };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 text-lg">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-[#0F203C]">Enquête Batardeaux</h1>
          <span className="text-base text-[#475569]">Étape {currentStep + 1} sur {steps.length}</span>
        </div>
        <Progress value={progress} className="h-2 bg-[#E2E8F0]" />
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-white rounded-md border border-[#E2E8F0] p-8 mb-6 shadow-sm">
          <h2 className="text-3xl font-bold text-[#0F203C] mb-6 tracking-tight">
            {steps[currentStep].title}
          </h2>
          {steps[currentStep].render(stepProps)}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            data-testid="previous-button"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            variant="outline"
            className="border-[#E2E8F0] hover:bg-[#F8FAFC] text-base"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button
              data-testid="next-button"
              onClick={handleNext}
              className="bg-[#0F203C] hover:bg-[#0a1526] text-white text-base"
            >
              Suivant
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="submit-button"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-[#0F203C] hover:bg-[#0a1526] text-white font-semibold text-base"
            >
              {loading ? 'Envoi en cours...' : 'Soumettre'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Reusable building blocks                                            */
/* ------------------------------------------------------------------ */

// Wraps a question: anchor id (for scroll/highlight), number, title, required
// asterisk and an error outline when the answer is missing.
const QuestionBlock = ({ id, number, title, required, hint, errors, children }) => {
  const inError = errors?.has(id);
  return (
    <div
      id={`q-${id}`}
      className={`scroll-mt-24 rounded-md ${inError ? 'ring-2 ring-[#DC2626] ring-offset-4 ring-offset-white' : ''}`}
    >
      <Label className="text-xl font-semibold text-[#0F203C] mb-4 block leading-snug">
        {number ? `${number}. ` : ''}{title}
        {required && <span className="text-[#DC2626]"> *</span>}
        {hint && <span className="text-base text-[#475569] block mt-2 font-normal">{hint}</span>}
      </Label>
      {children}
      {inError && <p className="text-base text-[#DC2626] mt-2">Cette question est obligatoire.</p>}
    </div>
  );
};

const RadioQuestion = ({ id, value, options, onChange, testid }) => (
  <RadioGroup data-testid={testid} value={value} onValueChange={onChange}>
    {options.map((opt, idx) => (
      <div key={opt} className="flex items-center space-x-2 p-3 rounded hover:bg-[#F8FAFC] transition-colors">
        <RadioGroupItem value={opt} id={`${id}-${idx}`} />
        <Label htmlFor={`${id}-${idx}`} className="cursor-pointer flex-1 text-lg">{opt}</Label>
      </div>
    ))}
  </RadioGroup>
);

const CheckboxQuestion = ({ id, options, selected, onToggle }) => (
  <div className="space-y-3">
    {options.map((opt, idx) => (
      <div key={opt} className="flex items-center space-x-3 p-3 rounded hover:bg-[#F8FAFC] transition-colors">
        <Checkbox
          id={`${id}-${idx}`}
          checked={(selected || []).includes(opt)}
          onCheckedChange={(checked) => onToggle(opt, checked)}
        />
        <Label htmlFor={`${id}-${idx}`} className="cursor-pointer flex-1 text-lg">{opt}</Label>
      </div>
    ))}
  </div>
);

// Conditional "Précisez" field for "Autre"-type options (Rq3, Rq10).
const OtherInput = ({ value, onChange }) => (
  <Input
    className={`mt-3 ${INPUT_CLASS}`}
    placeholder="Précisez votre réponse"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
  />
);

// Matrix question: one labelled scale per item. Used for Q12 (simplicity scale)
// and the affirmations question (agreement scale).
const ScaleMatrix = ({ items, options, formData, updateField }) => (
  <div className="space-y-6 bg-[#F8FAFC] p-5 rounded-md">
    {items.map(({ key, label }) => (
      <div key={key}>
        <Label className="text-lg font-medium text-[#0F203C] mb-2 block leading-snug">{label}</Label>
        <RadioGroup
          value={formData[key]}
          onValueChange={(val) => updateField(key, val)}
          className="flex flex-wrap gap-x-6 gap-y-2"
        >
          {options.map((opt) => (
            <div key={opt} className="flex items-center space-x-2">
              <RadioGroupItem value={opt} id={`${key}-${opt}`} />
              <Label htmlFor={`${key}-${opt}`} className="cursor-pointer text-base">{opt}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    ))}
  </div>
);

const makeToggle = (formData, updateField) => (field, value, checked) => {
  const updated = checked
    ? [...(formData[field] || []), value]
    : (formData[field] || []).filter((v) => v !== value);
  updateField(field, updated);
};

/* ------------------------------------------------------------------ */
/* Option lists & matrix items                                         */
/* ------------------------------------------------------------------ */

const Q12_ITEMS = [
  { key: 'q12_comprehension', label: 'Comprendre la procédure de demande de subvention pour obtenir vos batardeaux à moindre coût' },
  { key: 'q12_dossier', label: 'Élaborer le dossier de demande de subvention' },
  { key: 'q12_accord', label: 'Obtenir un accord de subvention' },
  { key: 'q12_trouver', label: 'Trouver et contacter des installateurs de batardeaux' },
  { key: 'q12_devis', label: 'Obtenir des devis et un rendez-vous auprès des installateurs de batardeaux' },
  { key: 'q12_intervention', label: 'Faire intervenir les installateurs de batardeaux' },
];
const Q12_SCALE = ['Très simple', 'Plutôt simple', 'Plutôt complexe', 'Très complexe', 'Vous ne savez pas'];

const Q17_ITEMS = [
  { key: 'q17_stockage', label: 'Vous avez facilement trouvé un endroit chez vous pour stocker vos batardeaux.' },
  { key: 'q17_savoir_quand', label: 'Vous savez quand vous devez installer vos batardeaux.' },
  { key: 'q17_manipulation', label: "En cas d'alerte inondation, vous êtes capable de manipuler et mettre en place vos batardeaux vous-même." },
  { key: 'q17_acces', label: "Vous pouvez facilement accéder à vos batardeaux en cas d'alerte." },
  { key: 'q17_entretien', label: 'Vous savez comment et quand entretenir vos batardeaux pour les conserver en bon état et maintenir leur efficacité.' },
];
const Q17_SCALE = ['Tout à fait', 'Plutôt', 'Plutôt pas', 'Pas du tout', 'Vous ne savez pas'];

const Q_INEFFICACITE_ITEMS = [
  "Le matériel n'est pas étanche",
  "La pose des batardeaux n'a pas été correctement effectuée",
  "Vous n'avez pas réussi à mettre en place vos batardeaux lors de l'inondation",
  "Vous n'avez pas installé vos batardeaux car vous n'avez pas été prévenu qu'une inondation allait avoir lieu",
  "L'inondation était trop importante, elle a dépassé la hauteur de vos batardeaux",
  "Vos batardeaux n'ont pas résisté : ils ont cédé durant l'inondation",
  "Vous n'étiez pas chez vous lors de l'inondation",
  "L'eau est entrée par ailleurs que les accès protégés par les batardeaux",
  'Autre',
  'Vous ne savez pas',
];

/* ------------------------------------------------------------------ */
/* Validators (return list of missing question-block ids)             */
/* ------------------------------------------------------------------ */

function validateFilter(d) {
  return isFilled(d.q0) ? [] : ['q0'];
}

function validateTheme1(d) {
  const missing = [];
  if (!isFilled(d.q1_first)) missing.push('q1_first');
  if (!isFilled(d.q2)) missing.push('q2');
  if (!isFilled(d.q3)) missing.push('q3');
  if (!isFilled(d.q4)) missing.push('q4');
  if (!isFilled(d.q5)) missing.push('q5');
  if (d.q5 === 'Oui' && !isFilled(d.q6)) missing.push('q6');
  if (!isFilled(d.q7)) missing.push('q7');
  return missing;
}

function validateTheme2(d) {
  const missing = [];
  if (!isFilledArr(d.q8)) missing.push('q8');
  if (!isFilled(d.q9)) missing.push('q9');
  if (!isFilled(d.q10_montant) || !isFilled(d.q10_nombre)) missing.push('q10');
  if (!isFilled(d.q11)) missing.push('q11');
  if (Q12_ITEMS.some((i) => !isFilled(d[i.key]))) missing.push('q12');
  if (!isFilledArr(d.q13)) missing.push('q13');
  if (!isFilled(d.q15)) missing.push('q15');
  if (d.q15 === 'Oui' && !isFilled(d.q_efficacite)) missing.push('q_efficacite');
  if (typeof d.q_efficacite === 'string' && d.q_efficacite.startsWith('Non') && !isFilledArr(d.q_inefficacite)) {
    missing.push('q_inefficacite');
  }
  if (!isFilledArr(d.q16)) missing.push('q16');
  if (Q17_ITEMS.some((i) => !isFilled(d[i.key]))) missing.push('q17');
  if (!isFilled(d.q18)) missing.push('q18');
  if (!isFilled(d.q19)) missing.push('q19');
  if (!isFilled(d.q20)) missing.push('q20');
  if (!isFilled(d.q21)) missing.push('q21');
  if (!isFilled(d.q22)) missing.push('q22');
  // q23 (free-text remark) is optional.
  return missing;
}

function validateTheme3(d) {
  const missing = [];
  if (!isFilled(d.q24)) missing.push('q24');
  if (!isFilledArr(d.q25)) missing.push('q25');
  if (!isFilled(d.q26)) missing.push('q26');
  // q27 (free-text) is optional.
  return missing;
}

function validateDemographics(d) {
  const missing = [];
  if (!isFilled(d.rs1)) missing.push('rs1');
  if (d.rs1 === 'Un particulier') {
    ['rs2', 'rs3', 'rs4', 'rs5'].forEach((k) => { if (!isFilled(d[k])) missing.push(k); });
  }
  if (!isFilled(d.rs6)) missing.push('rs6');
  if (!isFilled(d.rs7)) missing.push('rs7');
  if (d.rs7 === 'Oui' && !isFilled(d.rs8)) missing.push('rs8');
  if (!isFilled(d.rs9)) missing.push('rs9');
  const rs10Answered =
    [d.rs10_pfms, d.rs10_ppri, d.rs10_dicrim, d.rs10_pcs].includes('Oui') || d.rs10_aucun === 'Oui';
  if (!rs10Answered) missing.push('rs10');
  return missing;
}

/* ------------------------------------------------------------------ */
/* Step 0 — Filter                                                     */
/* ------------------------------------------------------------------ */

const FilterQuestion = ({ formData, updateField, errors }) => (
  <div className="space-y-6">
    <QuestionBlock
      id="q0"
      number="Q0"
      required
      errors={errors}
      title="Avez-vous réalisé un diagnostic de vulnérabilité d'inondation pour un bien dont vous êtes propriétaire ou dont vous avez la gérance ?"
    >
      <RadioQuestion
        id="q0"
        testid="q0-radio-group"
        value={formData.q0}
        onChange={(val) => updateField('q0', val)}
        options={['Oui, pour plusieurs biens', 'Oui, pour un bien', 'Non']}
      />
      {formData.q0 === 'Oui, pour plusieurs biens' && (
        <p className="mt-4 text-base text-[#475569] italic bg-[#0F203C]/5 p-4 rounded">
          Si vous avez réalisé un diagnostic de vulnérabilité d'inondation pour plusieurs biens, merci de penser, pour répondre à l'ensemble du questionnaire, au dernier bien pour lequel vous avez fait le diagnostic.
        </p>
      )}
    </QuestionBlock>
  </div>
);

/* ------------------------------------------------------------------ */
/* Step 1 — Theme 1                                                    */
/* ------------------------------------------------------------------ */

const Theme1Questions = ({ formData, updateField, errors }) => (
  <div className="space-y-8">
    <QuestionBlock
      id="q1_first"
      number="Q1"
      required
      errors={errors}
      title="Quels sont les 3 mots qui vous viennent à l'esprit quand on vous parle de batardeaux ?"
    >
      <div className="space-y-3">
        <Input data-testid="q1-first-input" placeholder="En premier ?" value={formData.q1_first || ''} onChange={(e) => updateField('q1_first', e.target.value)} className={INPUT_CLASS} />
        <Input data-testid="q1-second-input" placeholder="En deuxième ?" value={formData.q1_second || ''} onChange={(e) => updateField('q1_second', e.target.value)} className={INPUT_CLASS} />
        <Input data-testid="q1-third-input" placeholder="En troisième ?" value={formData.q1_third || ''} onChange={(e) => updateField('q1_third', e.target.value)} className={INPUT_CLASS} />
      </div>
    </QuestionBlock>

    <QuestionBlock id="q2" number="Q2" required errors={errors} title="Considérez-vous que votre bien est actuellement concerné par le risque d'inondation ?">
      <RadioQuestion id="q2" value={formData.q2} onChange={(val) => updateField('q2', val)}
        options={['Oui, tout à fait', 'Oui, plutôt', 'Non, plutôt pas', 'Non, pas du tout', 'Vous ne savez pas']} />
    </QuestionBlock>

    <QuestionBlock id="q3" number="Q3" required errors={errors} title="En quelle année avez-vous réalisé le diagnostic de vulnérabilité d'inondation ?">
      <Input data-testid="q3-input" type="text" placeholder="Année" value={formData.q3 || ''} onChange={(e) => updateField('q3', e.target.value)} className={`max-w-xs ${INPUT_CLASS}`} />
    </QuestionBlock>

    <QuestionBlock id="q4" number="Q4" required errors={errors} title="D'après le diagnostic, quelle est la hauteur d'eau maximale susceptible d'impacter votre bien en cas d'inondation ?">
      <RadioQuestion id="q4" value={formData.q4} onChange={(val) => updateField('q4', val)}
        options={['Moins de 50 cm', 'Entre 50 cm et 1m', "Plus d'1m", "Votre bien est hors d'eau d'après le diagnostic", 'Vous ne savez pas']} />
    </QuestionBlock>

    <QuestionBlock id="q5" number="Q5" required errors={errors} title="Vous a-t-on préconisé d'installer des batardeaux dans les conclusions du diagnostic ?">
      <RadioQuestion id="q5" value={formData.q5} onChange={(val) => updateField('q5', val)} options={['Oui', 'Non', 'Vous ne savez pas']} />
    </QuestionBlock>

    {formData.q5 === 'Oui' && (
      <QuestionBlock id="q6" number="Q6" required errors={errors} title="Selon le diagnostic, l'installation des batardeaux était-elle une recommandation ou une obligation ?">
        <RadioQuestion id="q6" value={formData.q6} onChange={(val) => updateField('q6', val)} options={['Une recommandation', 'Une obligation', 'Vous ne savez pas']} />
      </QuestionBlock>
    )}

    <QuestionBlock id="q7" number="Q7" required errors={errors} title="Avez-vous équipé votre bien de batardeaux ?">
      <RadioQuestion id="q7" testid="q7-radio-group" value={formData.q7} onChange={(val) => updateField('q7', val)} options={['Oui', 'Non', 'Vous ne savez pas']} />
    </QuestionBlock>
  </div>
);

/* ------------------------------------------------------------------ */
/* Step 2a — Theme 2 (equipped)                                        */
/* Display numbers Q8–Q24. Field keys are kept stable for the data     */
/* model; they do not all match the displayed number after inserts.    */
/* ------------------------------------------------------------------ */

const Theme2Questions = ({ formData, updateField, errors }) => {
  const toggle = makeToggle(formData, updateField);

  return (
    <div className="space-y-8">
      <QuestionBlock id="q8" number="Q8" required errors={errors} title="Pourquoi avez-vous décidé d'installer des batardeaux ? (Plusieurs réponses possibles)">
        <CheckboxQuestion
          id="q8"
          selected={formData.q8}
          onToggle={(opt, checked) => toggle('q8', opt, checked)}
          options={[
            'Par obligation règlementaire',
            'Vous avez déjà été inondé',
            'Vos voisins ou des proches ont déjà été inondés',
            'Vous avez suivi les conseils donnés dans votre diagnostic',
            'Vous avez été sensibilisé en participant à une action locale',
            'Une autre raison',
            'Vous ne savez pas',
          ]}
        />
        {hasOther(formData.q8) && <OtherInput value={formData.q8_autre} onChange={(v) => updateField('q8_autre', v)} />}
      </QuestionBlock>

      <QuestionBlock id="q9" number="Q9" required errors={errors} title="Combien de temps s'est écoulé entre la réalisation de votre diagnostic et votre décision d'acquérir des batardeaux ?">
        <RadioQuestion id="q9" value={formData.q9} onChange={(val) => updateField('q9', val)}
          options={['Dès la lecture de votre diagnostic', "Moins d'un mois", 'Entre 1 mois et 3 mois', 'Entre 4 et 6 mois', 'Entre 7 mois et 1 an', 'Entre 1 et 2 ans', 'Plus de 2 ans', 'Vous ne savez pas']} />
      </QuestionBlock>

      <QuestionBlock id="q10" number="Q10" required errors={errors} title="Concernant l'acquisition de vos batardeaux :">
        <div className="space-y-4">
          <div>
            <Label htmlFor="q10-montant" className="text-lg font-medium text-[#0F203C] mb-2 block">
              À combien estimez-vous le montant total de l'acquisition de ces batardeaux ? (en euros)
            </Label>
            <Input id="q10-montant" data-testid="q10-montant-input" type="number" min="0" placeholder="Montant en €" value={formData.q10_montant || ''} onChange={(e) => updateField('q10_montant', e.target.value)} className={`max-w-xs ${INPUT_CLASS}`} />
          </div>
          <div>
            <Label htmlFor="q10-nombre" className="text-lg font-medium text-[#0F203C] mb-2 block">
              Combien de batardeaux avez-vous installés ?
            </Label>
            <Input id="q10-nombre" data-testid="q10-nombre-input" type="number" min="0" placeholder="Nombre de batardeaux" value={formData.q10_nombre || ''} onChange={(e) => updateField('q10_nombre', e.target.value)} className={`max-w-xs ${INPUT_CLASS}`} />
          </div>
        </div>
      </QuestionBlock>

      <QuestionBlock id="q11" number="Q11" required errors={errors} title="Quel est le pourcentage d'aide financière publique (subvention) dont vous avez bénéficié pour acquérir vos batardeaux ?">
        <RadioQuestion id="q11" value={formData.q11} onChange={(val) => updateField('q11', val)}
          options={['Aucune aide', 'Moins de 40%', 'Entre 41% et 80%', 'Entre 81 et 99%', '100%, tout a été pris en charge', 'Vous ne savez pas']} />
      </QuestionBlock>

      <QuestionBlock
        id="q12"
        number="Q12"
        required
        errors={errors}
        title="Voici différentes étapes dans l'acquisition et l'installation des batardeaux. Pour chacune d'entre elles, indiquez si vous l'avez jugée très simple, plutôt simple, plutôt complexe ou très complexe."
      >
        <ScaleMatrix items={Q12_ITEMS} options={Q12_SCALE} formData={formData} updateField={updateField} />
      </QuestionBlock>

      <QuestionBlock id="q13" number="Q13" required errors={errors} title="Qui a réalisé les travaux (fabrication et installation des batardeaux) ? (Plusieurs réponses possibles)">
        <CheckboxQuestion
          id="q13"
          selected={formData.q13}
          onToggle={(opt, checked) => toggle('q13', opt, checked)}
          options={['Vous-même', 'Des proches (amis, famille, etc.)', 'Des professionnels', 'Autre', 'Vous ne savez pas']}
        />
        {hasOther(formData.q13) && <OtherInput value={formData.q13_autre} onChange={(v) => updateField('q13_autre', v)} />}
      </QuestionBlock>

      <QuestionBlock id="q15" number="Q14" required errors={errors} title="Avez-vous déjà utilisé vos batardeaux pour vous protéger d'une inondation ?">
        <RadioQuestion id="q15" value={formData.q15} onChange={(val) => updateField('q15', val)} options={['Oui', 'Non', 'Vous ne savez pas']} />
      </QuestionBlock>

      {formData.q15 === 'Oui' && (
        <QuestionBlock id="q_efficacite" number="Q15" required errors={errors} title="Les batardeaux ont-ils été efficaces pour empêcher l'eau d'entrer dans votre logement ?">
          <RadioQuestion id="q_efficacite" value={formData.q_efficacite} onChange={(val) => updateField('q_efficacite', val)}
            options={['Oui, tout à fait', 'Oui, plutôt', 'Non, plutôt pas', 'Non, pas du tout', 'Vous ne savez pas']} />
        </QuestionBlock>
      )}

      {typeof formData.q_efficacite === 'string' && formData.q_efficacite.startsWith('Non') && (
        <QuestionBlock id="q_inefficacite" number="Q16" required errors={errors} title="Pourquoi selon vous vos batardeaux n'ont-ils pas été efficaces ? (Plusieurs réponses possibles)">
          <CheckboxQuestion
            id="q_inefficacite"
            selected={formData.q_inefficacite}
            onToggle={(opt, checked) => toggle('q_inefficacite', opt, checked)}
            options={Q_INEFFICACITE_ITEMS}
          />
          {hasOther(formData.q_inefficacite) && <OtherInput value={formData.q_inefficacite_autre} onChange={(v) => updateField('q_inefficacite_autre', v)} />}
        </QuestionBlock>
      )}

      <QuestionBlock id="q16" number="Q17" required errors={errors} title="Quels sont les équipements complémentaires aux batardeaux que vous avez installés ? (Plusieurs réponses possibles)">
        <CheckboxQuestion
          id="q16"
          selected={formData.q16}
          onToggle={(opt, checked) => toggle('q16', opt, checked)}
          options={['Pompe de relevage', 'Clapet anti-retour', 'Protection des aérations', 'Surélévation des équipements', 'Aucun', 'Autre', 'Vous ne savez pas']}
        />
        {hasOther(formData.q16) && <OtherInput value={formData.q16_autre} onChange={(v) => updateField('q16_autre', v)} />}
      </QuestionBlock>

      <QuestionBlock
        id="q17"
        number="Q18"
        required
        errors={errors}
        title="Voici différentes affirmations relatives à vos batardeaux. Pour chacune d'entre elles, indiquez si elle vous correspond tout à fait, plutôt, plutôt pas ou pas du tout."
      >
        <ScaleMatrix items={Q17_ITEMS} options={Q17_SCALE} formData={formData} updateField={updateField} />
      </QuestionBlock>

      <QuestionBlock id="q18" number="Q19" required errors={errors} title="Êtes-vous capable de manipuler seul vos batardeaux ?">
        <RadioQuestion id="q18" value={formData.q18} onChange={(val) => updateField('q18', val)} options={['Oui', 'Non', 'Vous ne savez pas']} />
      </QuestionBlock>

      <QuestionBlock id="q19" number="Q20" required errors={errors} title="Avez-vous été formé à l'utilisation de vos batardeaux ?">
        <RadioQuestion id="q19" value={formData.q19} onChange={(val) => updateField('q19', val)} options={['Oui', 'Non', 'Vous ne savez pas']} />
      </QuestionBlock>

      <QuestionBlock id="q20" number="Q21" required errors={errors} title="Recommanderiez-vous l'installation de batardeaux à votre entourage s'il était concerné par le risque d'inondation (famille, amis, voisinage, etc.) ?">
        <RadioQuestion id="q20" value={formData.q20} onChange={(val) => updateField('q20', val)}
          options={['Oui, tout à fait', 'Oui, plutôt', 'Non, plutôt pas', 'Non, pas du tout', 'Vous ne savez pas']} />
      </QuestionBlock>

      <QuestionBlock id="q21" number="Q22" required errors={errors} title="Êtes-vous globalement satisfait par vos batardeaux ?">
        <RadioQuestion id="q21" value={formData.q21} onChange={(val) => updateField('q21', val)}
          options={['Oui, tout à fait', 'Oui, plutôt', 'Non, plutôt pas', 'Non, pas du tout', 'Vous ne savez pas']} />
      </QuestionBlock>

      <QuestionBlock id="q22" number="Q23" required errors={errors} title="Si c'était à refaire, installeriez-vous à nouveau des batardeaux ?">
        <RadioQuestion id="q22" value={formData.q22} onChange={(val) => updateField('q22', val)}
          options={['Oui, tout à fait', 'Oui, plutôt', 'Non, plutôt pas', 'Non, pas du tout', 'Vous ne savez pas']} />
      </QuestionBlock>

      <QuestionBlock id="q23" number="Q24" errors={errors} title="Avez-vous une remarque, suggestion ou critique concernant vos batardeaux ou le dispositif qui invite les personnes à en acquérir ?">
        <Textarea data-testid="q23-textarea" placeholder="Votre réponse (optionnel)" value={formData.q23 || ''} onChange={(e) => updateField('q23', e.target.value)} rows={4} className={INPUT_CLASS} />
      </QuestionBlock>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Step 2b — Theme 3 (not equipped)                                    */
/* ------------------------------------------------------------------ */

const Theme3Questions = ({ formData, updateField, errors }) => {
  const toggle = makeToggle(formData, updateField);

  return (
    <div className="space-y-8">
      <QuestionBlock id="q24" number="Q25" required errors={errors} title="Savez-vous qu'il existe des subventions et des appuis locaux pour vous permettre d'acquérir des batardeaux à moindre coût ?">
        <RadioQuestion id="q24" value={formData.q24} onChange={(val) => updateField('q24', val)}
          options={[
            'Oui, et vous vous êtes déjà renseigné à ce sujet',
            'Oui, mais vous ne vous êtes pas encore renseigné à ce sujet',
            'Non, mais cela est susceptible de vous intéresser',
            'Non, et cela ne vous intéresse pas',
            'Vous ne savez pas',
          ]} />
      </QuestionBlock>

      <QuestionBlock id="q25" number="Q26" required errors={errors} title="Pourquoi n'avez-vous pas équipé votre bien de batardeaux ? (Plusieurs réponses possibles)">
        <CheckboxQuestion
          id="q25"
          selected={formData.q25}
          onToggle={(opt, checked) => toggle('q25', opt, checked)}
          options={[
            'On ne vous a pas indiqué de vous protéger avec des batardeaux',
            "Vous n'avez pas les moyens financiers",
            "Vous n'avez pas eu le temps de vous en occuper",
            'Vous ne pensez pas que le risque soit important',
            'Les démarches administratives pour obtenir les subventions sont trop complexes',
            'Vous ne savez pas quel professionnel contacter',
            'Les installateurs ne sont pas disponibles',
            'Vous êtes trop âgé(e)',
            'Vous allez bientôt déménager',
            "Il s'agit de votre résidence secondaire",
            'Autre raison',
            'Vous ne savez pas',
          ]}
        />
        {hasOther(formData.q25) && <OtherInput value={formData.q25_autre} onChange={(v) => updateField('q25_autre', v)} />}
      </QuestionBlock>

      <QuestionBlock id="q26" number="Q27" required errors={errors} title="À la suite de votre diagnostic de vulnérabilité, votre intention d'installer des batardeaux a-t-elle évolué ?">
        <RadioQuestion id="q26" value={formData.q26} onChange={(val) => updateField('q26', val)}
          options={[
            "Oui, le diagnostic m'a donné envie d'en installer",
            "Oui, le diagnostic m'a au contraire dissuadé d'en installer",
            "Non, je n'envisageais pas d'en installer et c'est toujours le cas",
            "Non, j'envisageais déjà d'en installer et c'est toujours le cas",
            'Vous ne savez pas',
          ]} />
      </QuestionBlock>

      <QuestionBlock id="q27" number="Q28" errors={errors} title="Selon vous, comment la démarche d'acquisition des batardeaux pourrait-elle être optimisée pour vous permettre d'installer des batardeaux ?">
        <Textarea data-testid="q27-textarea" placeholder="Votre réponse (optionnel)" value={formData.q27 || ''} onChange={(e) => updateField('q27', e.target.value)} rows={4} className={INPUT_CLASS} />
      </QuestionBlock>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Step 3 — Demographics                                               */
/* ------------------------------------------------------------------ */

const DemographicQuestions = ({ formData, updateField, errors }) => {
  const setRs10 = (field, checked) => {
    if (field === 'rs10_aucun' && checked) {
      // "Je n'en connais aucun" is exclusive with the named documents.
      updateField('rs10_pfms', 'Non');
      updateField('rs10_ppri', 'Non');
      updateField('rs10_dicrim', 'Non');
      updateField('rs10_pcs', 'Non');
      updateField('rs10_aucun', 'Oui');
      return;
    }
    if (field !== 'rs10_aucun' && checked) updateField('rs10_aucun', 'Non');
    updateField(field, checked ? 'Oui' : 'Non');
  };

  return (
    <div className="space-y-8">
      <QuestionBlock id="rs1" required errors={errors} title="Vous répondez à ce questionnaire en tant que... ?">
        <RadioQuestion id="rs1" value={formData.rs1} onChange={(val) => updateField('rs1', val)}
          options={['Un particulier', 'Un(e) gestionnaire de bâtiments (syndic de copropriété, OPH, etc.)', 'Vous ne savez pas']} />
      </QuestionBlock>

      {formData.rs1 === 'Un particulier' && (
        <>
          <QuestionBlock id="rs2" required errors={errors} title="Quelle est votre situation vis-à-vis de ce logement ?">
            <RadioQuestion id="rs2" value={formData.rs2} onChange={(val) => updateField('rs2', val)}
              options={['Propriétaire en résidence principale', 'Propriétaire en résidence secondaire', 'Locataire', 'Occupant', 'Propriétaire bailleur', 'Autre']} />
            {formData.rs2 === 'Autre' && <OtherInput value={formData.rs2_autre} onChange={(v) => updateField('rs2_autre', v)} />}
          </QuestionBlock>

          <QuestionBlock id="rs3" required errors={errors} title="À quelle catégorie appartient ce logement ?">
            <RadioQuestion id="rs3" value={formData.rs3} onChange={(val) => updateField('rs3', val)}
              options={['Maison de plain pied', 'Maison à étage(s)', 'Appartement au rez-de-chaussée', 'Appartement en étage', 'Autre']} />
            {formData.rs3 === 'Autre' && <OtherInput value={formData.rs3_autre} onChange={(v) => updateField('rs3_autre', v)} />}
          </QuestionBlock>

          <QuestionBlock id="rs4" required errors={errors} title="Depuis combien de temps occupez-vous ce logement ?">
            <RadioQuestion id="rs4" value={formData.rs4} onChange={(val) => updateField('rs4', val)}
              options={["Moins d'1 an", 'Entre 1 et 5 ans', 'Entre 6 et 10 ans', 'Entre 11 et 20 ans', 'Plus de 20 ans']} />
          </QuestionBlock>

          <QuestionBlock id="rs5" required errors={errors} title="Quelle est votre tranche d'âge ?">
            <RadioQuestion id="rs5" value={formData.rs5} onChange={(val) => updateField('rs5', val)}
              options={['Moins de 25 ans', '25-34 ans', '35-49 ans', '50-64 ans', '65 ans et plus']} />
          </QuestionBlock>
        </>
      )}

      <QuestionBlock id="rs6" required errors={errors} title="Dans quelle commune se situe cette habitation / ce bâtiment ?">
        <Input data-testid="rs6-input" type="text" placeholder="Code postal" value={formData.rs6 || ''} onChange={(e) => updateField('rs6', e.target.value)} className={`max-w-xs ${INPUT_CLASS}`} />
      </QuestionBlock>

      <QuestionBlock id="rs7" required errors={errors} title="Avez-vous déjà connu une inondation dans cette habitation / ce bâtiment ?">
        <RadioQuestion id="rs7" value={formData.rs7} onChange={(val) => updateField('rs7', val)} options={['Oui', 'Non', 'Vous ne savez pas']} />
      </QuestionBlock>

      {formData.rs7 === 'Oui' && (
        <QuestionBlock id="rs8" required errors={errors} title="En quelle année avez-vous été inondé pour la dernière fois ?">
          <Input data-testid="rs8-input" type="text" placeholder="Année" value={formData.rs8 || ''} onChange={(e) => updateField('rs8', e.target.value)} className={`max-w-xs ${INPUT_CLASS}`} />
        </QuestionBlock>
      )}

      <QuestionBlock id="rs9" required errors={errors} title="Quelle est votre catégorie socioprofessionnelle ?">
        <RadioQuestion id="rs9" value={formData.rs9} onChange={(val) => updateField('rs9', val)}
          options={[
            'Agriculteur exploitant',
            "Artisan, commerçant, chef d'entreprise",
            'Cadre, profession intellectuelle supérieure',
            'Profession intermédiaire',
            'Employé',
            'Ouvrier',
            'Retraité',
            'Sans activité professionnelle',
            'Autre',
          ]} />
        {formData.rs9 === 'Autre' && <OtherInput value={formData.rs9_autre} onChange={(v) => updateField('rs9_autre', v)} />}
      </QuestionBlock>

      <QuestionBlock id="rs10" required errors={errors} title="Avez-vous connaissance des documents suivants ? (Plusieurs réponses possibles)">
        <div className="space-y-3">
          {[
            { key: 'rs10_pfms', label: 'PFMS (Plan Familial de Mise en Sûreté)' },
            { key: 'rs10_ppri', label: "PPRI (Plan de Prévention des Risques d'Inondation)" },
            { key: 'rs10_dicrim', label: "DICRIM (Document d'Information Communal sur les Risques Majeurs)" },
            { key: 'rs10_pcs', label: 'PCS (Plan Communal de Sauvegarde)' },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center space-x-3 p-3 rounded hover:bg-[#F8FAFC] transition-colors">
              <Checkbox id={key} checked={formData[key] === 'Oui'} onCheckedChange={(checked) => setRs10(key, checked)} />
              <Label htmlFor={key} className="cursor-pointer flex-1 text-lg">{label}</Label>
            </div>
          ))}
          <div className="flex items-center space-x-3 p-3 rounded hover:bg-[#F8FAFC] transition-colors">
            <Checkbox id="rs10_aucun" checked={formData.rs10_aucun === 'Oui'} onCheckedChange={(checked) => setRs10('rs10_aucun', checked)} />
            <Label htmlFor="rs10_aucun" className="cursor-pointer flex-1 text-lg">Je n'en connais aucun</Label>
          </div>
        </div>
      </QuestionBlock>
    </div>
  );
};

export default Survey;
